/**
 * HTTP API 客戶端
 *
 * 集中管理所有 HTTP 請求，提供：
 * - 統一的錯誤處理
 * - 自動附加認證 Token
 * - 請求/回應攔截
 */

import { CONFIG } from '@/config';
import { getAuthToken } from '@/store/auth-store';
import { getCsrfToken } from '@/utils/csrf';
import { SECURITY_CONSTANTS } from '@/utils/security';

// ========================================================================
// 型別定義
// ========================================================================

/** API 錯誤類別 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public data?: unknown
  ) {
    super(detail);
    this.name = 'ApiError';
  }
}

/** 請求選項 */
export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** 請求參數（會轉換為 query string） */
  params?: Record<string, string | number | boolean | undefined>;
  /** 請求超時（毫秒） */
  timeout?: number;
  /** 是否需要認證 */
  requireAuth?: boolean;
  /** 是否跳過錯誤處理 */
  skipErrorHandling?: boolean;
}

/** JSON 請求選項 */
export interface JsonRequestOptions extends RequestOptions {
  body?: unknown;
}

/** FormData 請求選項 */
export interface FormDataRequestOptions extends RequestOptions {
  body?: FormData;
}

// ========================================================================
// 輔助函式
// ========================================================================

/**
 * 建立帶有超時的 AbortSignal
 */
function createTimeoutSignal(timeout: number): AbortSignal {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), timeout);
  return controller.signal;
}

/**
 * 建立查詢字串
 */
function createQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      searchParams.append(key, String(value));
    }
  }
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * 取得認證和安全標頭
 * 包含 Authorization 和 CSRF Token
 */
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  // 加入 Bearer Token
  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // 加入 CSRF Token（用於防止 CSRF 攻擊）
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers[SECURITY_CONSTANTS.CSRF_HEADER_NAME] = csrfToken;
  }

  return headers;
}

// ========================================================================
// API 客戶端類別
// ========================================================================

class ApiClient {
  private baseUrl: string;
  private defaultTimeout: number;

  constructor(baseUrl: string, defaultTimeout = 30000) {
    this.baseUrl = baseUrl;
    this.defaultTimeout = defaultTimeout;
  }

  /**
   * 發送 GET 請求
   */
  async get<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * 發送 POST 請求（JSON body）
   */
  async post<T>(endpoint: string, options: JsonRequestOptions = {}): Promise<T> {
    const { body, ...restOptions } = options;
    return this.request<T>(endpoint, {
      ...restOptions,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...restOptions.headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * 發送 POST 請求（FormData body）
   */
  async postForm<T>(endpoint: string, options: FormDataRequestOptions = {}): Promise<T> {
    const { body, ...restOptions } = options;
    return this.request<T>(endpoint, {
      ...restOptions,
      method: 'POST',
      // 不設定 Content-Type，讓瀏覽器自動處理 boundary
      body: body as unknown as BodyInit,
    });
  }

  /**
   * 發送 PUT 請求
   */
  async put<T>(endpoint: string, options: JsonRequestOptions = {}): Promise<T> {
    const { body, ...restOptions } = options;
    return this.request<T>(endpoint, {
      ...restOptions,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...restOptions.headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * 發送 DELETE 請求
   */
  async delete<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * 通用請求方法
   */
  private async request<T>(
    endpoint: string,
    options: RequestOptions & { method: string; body?: BodyInit }
  ): Promise<T> {
    const {
      params,
      timeout = this.defaultTimeout,
      requireAuth = true,
      skipErrorHandling = false,
      ...fetchOptions
    } = options;

    // 建立完整 URL
    const queryString = params ? createQueryString(params) : '';
    const url = `${this.baseUrl}${endpoint}${queryString}`;

    // 合併標頭
    const headers: Record<string, string> = {
      ...(requireAuth ? getAuthHeaders() : {}),
      ...(fetchOptions.headers as Record<string, string>),
    };

    // 發送請求
    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        signal: createTimeoutSignal(timeout),
      });

      // 處理錯誤回應
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail || errorData.message || `HTTP ${response.status}`;

        if (!skipErrorHandling) {
          console.error(`[API] ${options.method} ${endpoint} failed:`, detail);
        }

        throw new ApiError(response.status, detail, errorData);
      }

      // 處理空回應
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return {} as T;
      }

      return response.json();
    } catch (error) {
      // 處理超時
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ApiError(408, '請求超時');
      }

      // 處理網路錯誤
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new ApiError(0, '網路連線失敗，請檢查網路狀態');
      }

      // 重新拋出 ApiError
      if (error instanceof ApiError) {
        throw error;
      }

      // 其他錯誤
      throw new ApiError(500, error instanceof Error ? error.message : '未知錯誤');
    }
  }
}

// ========================================================================
// 匯出
// ========================================================================

/** API 客戶端實例 */
export const apiClient = new ApiClient(CONFIG.API_BASE_URL);

/**
 * 建立新的 API 客戶端（用於不同的 base URL）
 */
export function createApiClient(baseUrl: string, timeout?: number): ApiClient {
  return new ApiClient(baseUrl, timeout);
}
